import React from 'react';
import styled, { keyframes } from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import SpeedIcon from '@material-ui/icons/Speed';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import StarIcon from '@material-ui/icons/Star';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import InfoIcon from '@material-ui/icons/Info';
import TwoWheelerIcon from '@material-ui/icons/TwoWheeler';
import { DELIVERY_ZONES, PRIMARY_CITY, formatCurrency } from '../config/market';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 50%, #0f1a1c 100%);
  padding: var(--lusuku-nav-h, 120px) 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: #f8f9fa;
    border-radius: 100% 100% 0 0;
    transform: translateY(50%);
  }

  @media (max-width: 768px) {
    padding: var(--lusuku-nav-h, 140px) 16px 120px;
    
    &::after {
      height: 50px;
    }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 193, 7, 0.15);
  border: 2px solid rgba(255, 193, 7, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: ${float} 3s ease-in-out infinite;

  svg {
    font-size: 40px;
    color: #FFC107;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ContentSection = styled.section`
  padding: 60px 20px 80px;
  
  @media (max-width: 768px) {
    padding: 40px 16px 60px;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 16px;

  span {
    color: #fc5c65;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SectionSubtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 48px;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 32px;
  }
`;

const ShippingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ShippingCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.$delay || '0s'};
  position: relative;
  overflow: hidden;

  ${props => props.$featured && `
    border-color: #FFC107;
    background: linear-gradient(135deg, #fffef5 0%, #fff 100%);
  `}

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: linear-gradient(135deg, #FFC107 0%, #FF9800 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 4px;
  animation: ${pulse} 2s ease-in-out infinite;

  svg {
    font-size: 14px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.$bg || 'linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)'};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 30px;
    color: white;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

const CardDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const PriceTag = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 20px;
`;


const Price = styled.span`
  font-size: 2.5rem;
  font-weight: 800;
  color: #324D54;
`;

const PriceUnit = styled.span`
  font-size: 0.9rem;
  color: #999;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  color: #555;
  font-size: 0.95rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: #10B981;
    font-size: 18px;
    flex-shrink: 0;
  }
`;

const DeliveryTime = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 12px;
  margin-top: 20px;

  svg {
    color: #fc5c65;
    font-size: 20px;
  }

  span {
    color: #1a1a1a;
    font-weight: 600;
    font-size: 0.95rem;
  }
`;

const InfoSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const InfoCard = styled.div`
  text-align: center;
`;

const InfoIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, rgba(252, 92, 101, 0.1) 0%, rgba(252, 92, 101, 0.05) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;

  svg {
    font-size: 28px;
    color: #fc5c65;
  }
`;

const InfoTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
`;

const ZonesSection = styled.div`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 100%);
  border-radius: 20px;
  padding: 40px;
  color: white;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ZonesTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #FFC107;
  }
`;

const ZonesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ZoneTag = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px 16px;
  border-radius: 10px;
  text-align: center;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: #FFC107;
  }
`;

const NoteBox = styled.div`
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  padding: 20px 24px;
  margin-top: 40px;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  svg {
    color: #FFC107;
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const NoteText = styled.p`
  color: #5a4a00;
  font-size: 0.95rem;
  line-height: 1.7;
  margin: 0;

  strong {
    color: #3d3200;
  }
`;

const shippingPlans = [
  {
    id: 'standard',
    name: 'Standard Delivery',
    icon: <LocalShippingIcon />,
    iconBg: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
    price: '5,000 - 10,000',
    description: 'Perfect for non-urgent orders. Reliable delivery at the most affordable rate.',
    deliveryTime: '1-3 Business Days',
    features: [
      'Affordable rates for all zones',
      'Real-time order tracking',
      'SMS delivery notifications',
      'Contactless delivery option'
    ]
  },
  {
    id: 'expedited',
    name: 'Expedited Delivery',
    icon: <SpeedIcon />,
    iconBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    price: '10,000',
    description: 'Faster delivery for when you need your items sooner. Priority handling guaranteed.',
    deliveryTime: '1-2 Business Days',
    features: [
      'Priority order processing',
      'Faster dispatch times',
      'Live GPS tracking',
      'Dedicated support line'
    ]
  },
  {
    id: 'sameday',
    name: 'Same Day Delivery',
    icon: <FlashOnIcon />,
    iconBg: 'linear-gradient(135deg, #fc5c65 0%, #eb3b5a 100%)',
    price: '15,000',
    description: 'Need it today? Get your order delivered within hours of placing it.',
    deliveryTime: 'Within 1-3 Hours',
    features: [
      'Order by 2 PM for same-day',
      'Express priority handling',
      'Real-time rider tracking',
      'Instant delivery confirmation'
    ]
  },
  {
    id: 'prime',
    name: 'Lusuku Prime',
    icon: <StarIcon />,
    iconBg: 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)',
    price: '30,000',
    description: 'Our premium subscription for unlimited same-day deliveries all month long!',
    deliveryTime: 'Unlimited Same-Day',
    featured: true,
    features: [
      'Unlimited free same-day delivery',
      'Zero delivery charges all month',
      'Priority customer support',
      'Exclusive member discounts',
      'Early access to promotions'
    ]
  }
];

const deliveryZones = DELIVERY_ZONES;

const formatPlanPrice = (price) => {
  if (String(price).includes(" - ")) {
    return String(price)
      .split(" - ")
      .map((part) => formatCurrency(Number(String(part).replace(/,/g, ""))))
      .join(" - ");
  }
  return formatCurrency(Number(String(price).replace(/,/g, "")));
};

const ShippingMatrix = () => {
  return (
    <PageWrapper>
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroIcon>
            <TwoWheelerIcon />
          </HeroIcon>
          <HeroTitle>Shipping & Delivery</HeroTitle>
          <HeroSubtitle>
            Fast, reliable delivery across {PRIMARY_CITY} and beyond. Choose the shipping 
            option that works best for you and track your order in real-time.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Content Section */}
      <ContentSection>
        <Container>
          <SectionTitle>Choose Your <span>Delivery Plan</span></SectionTitle>
          <SectionSubtitle>
            From budget-friendly standard shipping to lightning-fast same-day delivery, 
            we have options to fit every need and timeline.
          </SectionSubtitle>

          {/* Shipping Plans Grid */}
          <ShippingGrid>
            {shippingPlans.map((plan, index) => (
              <ShippingCard 
                key={plan.id} 
                $featured={plan.featured}
                $delay={`${0.1 + index * 0.1}s`}
              >
                {plan.featured && (
                  <FeaturedBadge>
                    <StarIcon /> Best Value
                  </FeaturedBadge>
                )}
                <CardHeader>
                  <CardIcon $bg={plan.iconBg}>
                    {plan.icon}
                  </CardIcon>
                  <CardTitle>{plan.name}</CardTitle>
                </CardHeader>
                <CardDescription>{plan.description}</CardDescription>
                <PriceTag>
                  <Price>{formatPlanPrice(plan.price)}</Price>
                  {plan.featured && <PriceUnit>/month</PriceUnit>}
                </PriceTag>
                <FeaturesList>
                  {plan.features.map((feature, i) => (
                    <FeatureItem key={i}>
                      <CheckCircleIcon />
                      {feature}
                    </FeatureItem>
                  ))}
                </FeaturesList>
                <DeliveryTime>
                  <AccessTimeIcon />
                  <span>{plan.deliveryTime}</span>
                </DeliveryTime>
              </ShippingCard>
            ))}
          </ShippingGrid>

          {/* Info Section */}
          <InfoSection>
            <SectionTitle style={{ marginBottom: '32px' }}>How It <span>Works</span></SectionTitle>
            <InfoGrid>
              <InfoCard>
                <InfoIconWrapper>
                  <LocalShippingIcon />
                </InfoIconWrapper>
                <InfoTitle>Place Your Order</InfoTitle>
                <InfoText>
                  Shop from any of our vendors and select your preferred delivery option at checkout.
                </InfoText>
              </InfoCard>
              <InfoCard>
                <InfoIconWrapper>
                  <TwoWheelerIcon />
                </InfoIconWrapper>
                <InfoTitle>We Dispatch</InfoTitle>
                <InfoText>
                  Our riders pick up your order and head straight to your location with care.
                </InfoText>
              </InfoCard>
              <InfoCard>
                <InfoIconWrapper>
                  <CheckCircleIcon />
                </InfoIconWrapper>
                <InfoTitle>Receive & Enjoy</InfoTitle>
                <InfoText>
                  Get notified when your rider arrives. Enjoy your items delivered fresh!
                </InfoText>
              </InfoCard>
            </InfoGrid>
          </InfoSection>

          {/* Delivery Zones */}
          <ZonesSection>
            <ZonesTitle>
              <LocationOnIcon />
              Delivery Coverage Areas
            </ZonesTitle>
            <ZonesGrid>
              {deliveryZones.map((zone, index) => (
                <ZoneTag key={index}>{zone}</ZoneTag>
              ))}
            </ZonesGrid>
          </ZonesSection>

          {/* Note */}
          <NoteBox>
            <InfoIcon />
            <NoteText>
              <strong>Note:</strong> Delivery fees may vary based on distance and order size. 
              For locations outside our standard coverage area, additional charges may apply. 
              Contact our support team for custom delivery arrangements.
            </NoteText>
          </NoteBox>

        </Container>
      </ContentSection>

      <Footer />
    </PageWrapper>
  );
};

export default ShippingMatrix;
